/**
 * AI Recruit - Bulk Upload Context (Candidates page)
 *
 * Job-less sibling of BulkApplyContext. Hosts the bulk-upload dialog and its
 * minimized tray ABOVE the route tree, so a running batch survives navigation
 * — mirrors exactly why BulkApplyContext exists for the job-apply flow.
 *
 * Mounted inside <Router> deliberately: DuplicateDetectionModal uses
 * useNavigate(), so the host needs router context.
 *
 * Scope: survives client-side navigation, NOT a full page reload. Browser File
 * objects can't be serialised, so a queued-but-not-yet-uploaded file cannot be
 * restored after F5. Anything already uploaded keeps processing regardless.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'

import BulkUploadResumesModal from '@/components/resume/BulkUploadResumesModal'

interface BulkUploadContextValue {
  /** Open the bulk upload dialog. Blocked if a batch is already open. */
  openBulkUpload: () => void
  isOpen: boolean
}

const BulkUploadContext = createContext<BulkUploadContextValue | null>(null)

export const useBulkUpload = (): BulkUploadContextValue => {
  const ctx = useContext(BulkUploadContext)
  if (!ctx) throw new Error('useBulkUpload must be used within a BulkUploadProvider')
  return ctx
}

export const BulkUploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const openBulkUpload = useCallback(() => {
    setOpen((prev) => {
      if (prev) {
        toast.error('The Upload Resume(s) dialog is already open. Finish or close it before starting a new one.', {
          duration: 5000,
        })
        return prev
      }
      return true
    })
  }, [])

  const value = useMemo<BulkUploadContextValue>(() => ({
    openBulkUpload,
    isOpen: open,
  }), [openBulkUpload, open])

  return (
    <BulkUploadContext.Provider value={value}>
      {children}

      {/* Rendered outside the routes, so navigation never unmounts a live batch. */}
      {open && (
        <BulkUploadResumesModal
          isOpen
          onClose={() => setOpen(false)}
          onCompleted={() => {
            queryClient.invalidateQueries({ queryKey: ['resumes'] })
          }}
        />
      )}
    </BulkUploadContext.Provider>
  )
}

export default BulkUploadProvider
